import { Box, Button } from '@cloudscape-design/components';
import PPartDisplay from 'components/Projects/pPartDisplay';
import PropTypes from 'prop-types';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import useLot from '../hooks/useLot';
import LotKindBadge from './LotKindBadge';
import LotStatusBadge from './LotStatusBadge';

/**
 * Renders a lot, from a doc ID (looked up via the cached useLot query,
 * so repeated cells for the same lot share one fetch) or from a lot
 * model the caller already has (no fetching). By default just the code,
 * optionally as a link; the show* props extend it to the standard
 * summary row — code link · part label · badges — used by the trace
 * tree and a lot's "Made from" sources. A missing code (or a failed
 * lookup) degrades to whatever is available, or an em dash.
 */
export default function LotDisplay({
  lot: lotProp = null,
  lotId = null,
  link = false,
  showPart = false,
  showKind = false,
  showStatus = false,
  showQty = false,
}) {
  const navigate = useNavigate();
  // Skip the fetch when the caller already has the doc.
  const { data: fetchedLot, isLoading } = useLot(lotProp ? null : lotId);
  const lot = lotProp || fetchedLot;

  if (!lotProp && !lotId) return '—';
  if (isLoading) return 'Loading...';

  const rich = showPart || showKind || showStatus || showQty;
  // Plain mode keeps the original contract: nothing to show → em dash.
  if (!rich && !lot?.code) return '—';

  const text = lot?.code || lot?.id || lotId;
  const textNode = link ? (
    <Button variant="inline-link" onClick={() => navigate(`/lots/${lot?.id || lotId}`)}>
      {text}
    </Button>
  ) : text;

  if (!rich) return textNode;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap',
    }}
    >
      {textNode}
      {showPart && (
        <PPartDisplay
          fieldName="fullId"
          projectDocId={lot?.projectDocId}
          pPartDocId={lot?.pPartDocId}
          fallbackMessage="—"
        />
      )}
      {showKind && <LotKindBadge kind={lot?.kind} />}
      {showStatus && <LotStatusBadge status={lot?.status} />}
      {showQty && (
        <Box color="text-body-secondary" fontSize="body-s">
          {`${lot?.remainingQty ?? '—'} / ${lot?.initialQty ?? '—'} remaining`}
        </Box>
      )}
    </div>
  );
}

LotDisplay.propTypes = {
  lot: PropTypes.shape({
    id: PropTypes.string.isRequired,
    code: PropTypes.string,
    kind: PropTypes.string,
    status: PropTypes.string,
    remainingQty: PropTypes.number,
    initialQty: PropTypes.number,
    projectDocId: PropTypes.string,
    pPartDocId: PropTypes.string,
  }),
  lotId: PropTypes.string,
  link: PropTypes.bool,
  showPart: PropTypes.bool,
  showKind: PropTypes.bool,
  showStatus: PropTypes.bool,
  showQty: PropTypes.bool,
};
